import { computed } from "vue";
import { getUsersToAddFromPotentiallyProtectable } from "@/_store/tickets/adapters";
import type { TicketDetails } from "@/_store/tickets/tickets.module";
import { getFormattedDateTime } from "@/_helpers/date";
import { useTicketsStore } from "@/_store/tickets/tickets.module";
import { useUsersSettingsTabStore } from "@/_store/users-settings/users-tab.module";
import { debounce } from "lodash";
import type { TicketsModal } from "@/constants/tickets";
import { useDialogsStore } from "@/_store/dialogs.module";
import { Service } from "@/constants/cloud-apps";
import type { TicketUserToAddToProtection } from "@/types.ts";
import { isWorkspaceFrozenOrActionRestricted } from "@/_helpers/permissions.ts";
import { listDialogConstructor } from "@/_helpers/dialog-helpers.ts";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/constants/roles.ts";

export function useTicketPreviewGeneralInfo(ticketDetails: TicketDetails) {
  const ticketsStore = useTicketsStore();
  const dialogsStore = useDialogsStore();

  const isSoc = computed(() => ticketsStore.isSoc);

  const showAddToProtectionBtn = computed(() => {
    const showActionBtn = ticketDetails.actions?.length > 0;
    return showActionBtn && ticketDetails.potentiallyProtectableUsers.length > 0;
  });

  const usersToAdd = computed(
    () => getUsersToAddFromPotentiallyProtectable(ticketDetails.potentiallyProtectableUsers).users
  );

  const addUsersNotAllowed = computed(() =>
    isWorkspaceFrozenOrActionRestricted(
      RolePermissionsScope.WORKSPACE_MANAGEMENT,
      WorkspaceManagementScopeSections.USERS
    )
  );

  const addUsersToProtection = async (users: TicketUserToAddToProtection[]) => {
    await useUsersSettingsTabStore().addUsers({ users });
  };

  const addUsers = debounce(async (users: { email: string; service: Service }[]) => {
    if (addUsersNotAllowed.value) return;
    await addUsersToProtection(users);
  }, 500);

  const openListModal = <T>(modal: TicketsModal, items: T[]) => {
    dialogsStore.openDialog(listDialogConstructor({ action: modal, item: { items } }));
  };

  return {
    getFormattedDateTime,
    isSoc,
    showAddToProtectionBtn,
    usersToAdd,
    addUsersNotAllowed,
    addUsers,
    openListModal,
    Service,
  };
}
